/**
 * LevNutri — Figura do corpo com as medidas apontadas (documento da paciente).
 *
 * Silhueta por sexo (/silhueta-mulher.png, /silhueta-homem.png, fundo
 * transparente). Cada circunferência da antropometria vira uma etiqueta na
 * lateral ligada por linha ao ponto do corpo. Unidades em pt (impressão A4).
 */
import React from 'react';

// Posição de cada medida na imagem (fração da largura/altura) e lado da etiqueta.
const PONTOS = {
  F: {
    circ_pescoco:     { x: 0.50, y: 0.145, lado: 'L' },
    circ_torax:       { x: 0.52, y: 0.255, lado: 'R' },
    circ_braco:       { x: 0.82, y: 0.150, lado: 'R' },
    circ_antebraco:   { x: 0.17, y: 0.370, lado: 'L' },
    circ_cintura:     { x: 0.47, y: 0.370, lado: 'R' },
    circ_abdomen:     { x: 0.46, y: 0.410, lado: 'L' },
    circ_quadril:     { x: 0.47, y: 0.490, lado: 'R' },
    circ_coxa:        { x: 0.62, y: 0.620, lado: 'R' },
    circ_panturrilha: { x: 0.68, y: 0.800, lado: 'R' },
  },
  M: {
    circ_pescoco:     { x: 0.49, y: 0.240, lado: 'L' },
    circ_torax:       { x: 0.49, y: 0.360, lado: 'R' },
    circ_braco:       { x: 0.11, y: 0.470, lado: 'L' },
    circ_antebraco:   { x: 0.09, y: 0.720, lado: 'L' },
    circ_cintura:     { x: 0.49, y: 0.600, lado: 'R' },
    circ_abdomen:     { x: 0.49, y: 0.665, lado: 'L' },
    circ_quadril:     { x: 0.49, y: 0.745, lado: 'R' },
    circ_coxa:        { x: 0.77, y: 0.850, lado: 'R' },
    // Panturrilha fica fora da figura masculina (cortada no joelho): só na lista.
  },
};

const PROPORCAO = { F: 325 / 667, M: 627 / 1119 }; // largura / altura das imagens

// Medidas em que diminuir é o resultado esperado (verde quando cai).
const MELHOR_CAIR = ['circ_pescoco', 'circ_cintura', 'circ_abdomen', 'circ_quadril'];

const COR = { texto: '#2f2a26', suave: '#736b64', linha: '#9a6a42', bom: '#46714f', ruim: '#a8483f' };
const FONTE = "'Nunito', sans-serif";

export const fmtBR = (v, casas = 1) =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas });

/** Lista das circunferências presentes (valor + variação desde a primeira avaliação). */
export function medidasDoCorpo(ultima, primeira, gender) {
  if (!ultima) return [];
  const braco = ultima.circ_braco_contraido != null && ultima.circ_braco_contraido !== ''
    ? 'circ_braco_contraido' : 'circ_braco_relaxado';
  const lista = [
    ['circ_pescoco', 'Pescoço', 'circ_pescoco'],
    ['circ_torax', gender === 'M' ? 'Tórax' : 'Busto', 'circ_torax'],
    ['circ_braco', braco === 'circ_braco_contraido' ? 'Braço contraído' : 'Braço', braco],
    ['circ_antebraco', 'Antebraço', 'circ_antebraco'],
    ['circ_cintura', 'Cintura', 'circ_cintura'],
    ['circ_abdomen', 'Abdômen', 'circ_abdomen'],
    ['circ_quadril', 'Quadril', 'circ_quadril'],
    ['circ_coxa', 'Coxa', 'circ_coxa'],
    ['circ_panturrilha', 'Panturrilha', 'circ_panturrilha'],
  ];
  return lista
    .filter(([, , campo]) => ultima[campo] != null && ultima[campo] !== '' && !isNaN(parseFloat(ultima[campo])))
    .map(([ponto, label, campo]) => {
      const v = parseFloat(ultima[campo]);
      const p0 = primeira && primeira !== ultima ? parseFloat(primeira[campo]) : NaN;
      const diff = isNaN(p0) ? null : v - p0;
      return { ponto, label, campo, valor: v, diff };
    });
}

/**
 * Figura com etiquetas. `medidas` vem de medidasDoCorpo(); `altura` em pt.
 * Retorna também as medidas que não têm ponto na figura (ex.: panturrilha no homem).
 */
export default function CorpoAnotado({ gender, medidas = [], largura = 300, altura = 320 }) {
  const g = gender === 'M' ? 'M' : 'F';
  const imgH = altura;
  const imgW = imgH * PROPORCAO[g];
  const imgX = (largura - imgW) / 2;
  const ETQ_W = 56;            // largura da etiqueta
  const ETQ_H = 24;            // altura mínima reservada por etiqueta (pt)

  const comPonto = medidas.filter(m => PONTOS[g][m.ponto]);
  const semPonto = medidas.filter(m => !PONTOS[g][m.ponto]);

  // Distribui as etiquetas de cada lado sem sobrepor (mantém a ordem vertical).
  const posicoes = {};
  ['L', 'R'].forEach(lado => {
    const doLado = comPonto
      .map(m => ({ m, p: PONTOS[g][m.ponto] }))
      .filter(o => o.p.lado === lado)
      .sort((a, b) => a.p.y - b.p.y);
    let minY = 0;
    doLado.forEach(({ m, p }) => {
      const alvo = p.y * imgH - ETQ_H / 2;
      const y = Math.max(alvo, minY);
      posicoes[m.ponto] = y;
      minY = y + ETQ_H + 3;
    });
    // Se estourou embaixo, empurra o grupo para cima.
    const estouro = minY - 3 - altura;
    if (estouro > 0) doLado.forEach(({ m }) => { posicoes[m.ponto] -= estouro; });
  });

  const corDiff = (m) => {
    if (m.diff == null || Math.abs(m.diff) < 0.05) return COR.suave;
    if (!MELHOR_CAIR.includes(m.ponto)) return COR.suave;
    return m.diff < 0 ? COR.bom : COR.ruim;
  };

  return (
    <div style={{ position: 'relative', width: `${largura}pt`, height: `${altura}pt`, margin: '0 auto' }}>
      <img
        src={g === 'M' ? '/silhueta-homem.png' : '/silhueta-mulher.png'}
        alt=""
        style={{ position: 'absolute', left: `${imgX}pt`, top: 0, height: `${imgH}pt`, width: `${imgW}pt` }}
      />

      <svg width={`${largura}pt`} height={`${altura}pt`} viewBox={`0 0 ${largura} ${altura}`}
        style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}>
        {comPonto.map(m => {
          const p = PONTOS[g][m.ponto];
          const px = imgX + p.x * imgW;
          const py = p.y * imgH;
          const ey = posicoes[m.ponto] + ETQ_H / 2;
          const ex = p.lado === 'L' ? ETQ_W : largura - ETQ_W;
          return (
            <g key={m.ponto}>
              <polyline points={`${ex},${ey} ${ex + (p.lado === 'L' ? 8 : -8)},${ey} ${px},${py}`}
                fill="none" stroke={COR.linha} strokeWidth="0.8" />
              <circle cx={px} cy={py} r="2.4" fill="#ffffff" stroke={COR.linha} strokeWidth="1" />
            </g>
          );
        })}
      </svg>

      {comPonto.map(m => {
        const p = PONTOS[g][m.ponto];
        return (
          <div key={m.ponto} style={{
            position: 'absolute', top: `${posicoes[m.ponto]}pt`, width: `${ETQ_W}pt`,
            [p.lado === 'L' ? 'left' : 'right']: 0, textAlign: p.lado === 'L' ? 'right' : 'left',
            fontFamily: FONTE, lineHeight: 1.15, background: 'rgba(255,255,255,0.92)', borderRadius: '3pt', padding: '1pt 2pt',
            boxSizing: 'border-box',
          }}>
            <div style={{ fontSize: '6.5pt', color: COR.suave }}>{m.label}</div>
            <div style={{ fontSize: '9pt', fontWeight: 700, color: COR.texto }}>{fmtBR(m.valor)} cm</div>
            {m.diff != null && Math.abs(m.diff) >= 0.05 && (
              <div style={{ fontSize: '6.5pt', fontWeight: 700, color: corDiff(m) }}>
                {m.diff > 0 ? '+' : '−'}{fmtBR(Math.abs(m.diff))} cm
              </div>
            )}
          </div>
        );
      })}

      {semPonto.length > 0 && (
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: `${ETQ_W}pt`, textAlign: 'left', fontFamily: FONTE, lineHeight: 1.15 }}>
          {semPonto.map(m => (
            <div key={m.ponto} style={{ marginTop: '3pt' }}>
              <div style={{ fontSize: '6.5pt', color: COR.suave }}>{m.label}</div>
              <div style={{ fontSize: '9pt', fontWeight: 700, color: COR.texto }}>{fmtBR(m.valor)} cm</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
